import type Highcharts from "highcharts";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Setup } from "../../../../api/useSetups";
import t from "../../../../i18n";
import { zIndexChart } from "@ui/Highcharts/BetterHighcharts";
import seriesColors from "./seriesColors";
import computeRemainingSymptom from "../../../../domain/computeRemainingSymptom";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import type { IndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import type {
  FailureValueStatus,
  IndexedFailureMetadata,
} from "../../../../api/useFailureMetadata";
import type { MetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import type { QuantityIndex } from "../../../../api/useQuantities";
import { UNEXPLAINED_GAP_ID } from "./serieIds";

const fillColor: Highcharts.GradientColorObject = {
  linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
  stops: [
    [0, seriesColors.unexplainedSymptom], // start
    [1, seriesColors.fadedUnexplainedSymptom], // end
  ],
};

export default function getRemainingSymptomSerie({
  analyses,
  indexedFailureValues,
  indexedFailureMetadata,
  setups,
  includeSuspectedFailure,
  yAxisId,
  legendIndex,
  metrologyOverrides,
  quantityIndex,
}: {
  analyses: ActiveAnalysis[];
  indexedFailureValues: IndexedFailureValues;
  indexedFailureMetadata: IndexedFailureMetadata;
  setups: Setup[];
  includeSuspectedFailure: boolean;
  yAxisId: string;
  legendIndex: number;
  metrologyOverrides: MetrologyOverrides;
  quantityIndex: QuantityIndex;
}): Highcharts.SeriesArearangeOptions {
  const offsetBySetupIdAndQuantityId = new Map<
    string /*setupId*/,
    Map<string /*quantityId*/, number>
  >();
  for (const setup of setups) {
    const offsetByQuantityIdForSetup = new Map<string /*quantityId*/, number>();
    offsetBySetupIdAndQuantityId.set(setup.id, offsetByQuantityIdForSetup);
    for (const simulationOutputs of setup.simulationOutputs) {
      offsetByQuantityIdForSetup.set(
        simulationOutputs.quantityId,
        simulationOutputs.offsetExpected,
      );
    }
  }

  const status: Set<FailureValueStatus> = includeSuspectedFailure
    ? new Set(["CONFIRMED", "SUSPECTED", "TO_BE_ANALYSED"])
    : new Set(["CONFIRMED"]);

  const data: Highcharts.PointOptionsObject[] = analyses.reduce<Highcharts.PointOptionsObject[]>(
    (points, analysis, index) => {
      const previousAnalysis = analyses[index - 1];
      const setup = getAnalysisSetup(setups, analysis);

      if (previousAnalysis !== undefined && previousAnalysis.group !== analysis.group) {
        points.push({
          x: (previousAnalysis.date + analysis.date) / 2,
          high: undefined,
          low: undefined,
        });
      }

      points.push({
        x: analysis.date,
        high: computeRemainingSymptom(
          analysis,
          indexedFailureValues,
          setup,
          status,
          metrologyOverrides,
          indexedFailureMetadata,
          quantityIndex,
          offsetBySetupIdAndQuantityId,
        ),
        low: 0,
      });

      return points;
    },
    [],
  );

  return {
    id: UNEXPLAINED_GAP_ID, // in order to have same visible series
    yAxis: yAxisId,
    name: t("charts.legend.remaining_symptom"),
    color: seriesColors.unexplainedSymptom,
    fillColor,
    type: "arearange" as const,
    replicateInNavigator: true,
    data,
    showInLegend: true,
    legendIndex,
    zIndex: zIndexChart.areaSeries + 1,
  };
}
